import { ViewMode, ViewToggle } from '@/components/ViewToggle/ViewToggle';
import { EditIcon, IconButton, Input, TrashIcon } from '@/lib';
import { Select } from '@/lib/Selects';
import { SelectOption } from '@/lib/Selects/Select/types';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplatesFilterChip } from '../config';

type EmailTemplateListControlsProps = {
  templateFilters: TemplatesFilterChip[];
  selectedFilter: TemplatesFilterChip;
  onFilterChange: (filter: TemplatesFilterChip) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  filteredCount: number;
  totalCount: number;
  onEditHub: () => void;
  onDeleteHub: () => void;
};

export const EmailTemplateListControls: FC<EmailTemplateListControlsProps> = ({
  searchValue,
  onSearchChange,
  viewMode,
  onViewModeChange,
  filteredCount,
  totalCount,
  onEditHub,
  onDeleteHub,
  templateFilters,
  selectedFilter,
  onFilterChange,
}) => {
  const { t } = useTranslation('campaign');

  const filterOptions = useMemo<SelectOption[]>(
    () =>
      templateFilters.map((filter) => ({
        id: filter,
        label: filter === 'All' ? t('emailWorkspace.templates.filterAll') : filter,
      })),
    [templateFilters, t]
  );

  const selectedFilterOption = useMemo(() => filterOptions.find((option) => option.id === selectedFilter) ?? null, [filterOptions, selectedFilter]);

  return (
    <div className='space-y-3 rounded-2xl border border-secondary-border bg-white p-4'>
      <div className='flex items-center justify-between gap-3'>
        <p className='text-xs font-semibold uppercase tracking-[0.16em] text-quaternary-500'>{t('emailWorkspace.templates.templateControls')}</p>
        <p className='text-sm text-secondary-600'>
          {filteredCount} of {totalCount}
        </p>
      </div>
      <div className='flex flex-col gap-3'>
        <div className='flex w-full flex-wrap items-center justify-between gap-4 md:flex-nowrap'>
          <div className='flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:flex-1'>
            <Input
              type='text'
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={t('emailWorkspace.templates.searchPlaceholder')}
              className='h-10 w-full border-secondary-border bg-white text-sm text-secondary-500 sm:flex-1 md:max-w-[350px]'
            />
          </div>
          <div className='flex flex-row items-center justify-end gap-4 flex-wrap md:flex-nowrap'>
            <IconButton
              type='button'
              intent='secondary'
              className='h-10 w-full shrink-0 justify-center sm:w-auto'
              leadingIcon={<EditIcon className='size-4' />}
              text={t('emailWorkspace.templates.editTemplatesHub')}
              onClick={onEditHub}
            />
            <IconButton
              type='button'
              intent='tertiary'
              className='h-10 w-full shrink-0 justify-center border border-red-600 text-red-600 hover:text-red-800 sm:w-auto'
              leadingIcon={<TrashIcon className='size-4' />}
              text={t('emailWorkspace.templates.deleteTemplatesHub')}
              onClick={onDeleteHub}
            />
          </div>
        </div>

        <div className='flex w-full items-center justify-between flex-wrap gap-4 md:flex-nowrap'>
          <Select
            items={filterOptions}
            value={selectedFilterOption}
            onChange={(item) => onFilterChange(String(item.id) as TemplatesFilterChip)}
            placeholder={t('emailWorkspace.templates.filterPlaceholder')}
            classNames={{
              wrapper: 'min-w-0 w-full md:w-[300px] capitalize',
              trigger: 'h-10 capitalize',
              footer: { wrapper: 'hidden' },
            }}
          />
          <ViewToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
        </div>
      </div>
    </div>
  );
};
